// O modelo do currículo: o tipo, os cinco temas e as regras de apresentação.
//
// Puro, sem UM import, com prova própria (`app/tests/curriculo.mjs`). A divisão
// com `lib/curriculo.ts` (que fala com o banco) é a de sempre na casa.
//
// ---------------------------------------------------------------------------
// O nome do tema mora AQUI, e em nenhum outro lugar
// ---------------------------------------------------------------------------
// No app de origem ele morava em dois: `TEMPLATES[].name` na galeria e um
// `switch` em `currentTemplateName()`. Duas listas das mesmas cinco strings, que
// é como uma passa a discordar da outra sem ninguém notar. Aqui a galeria, o
// rótulo do editor e o painel leem `TEMAS`.
//
// ---------------------------------------------------------------------------
// E o tema desconhecido não existe mais
// ---------------------------------------------------------------------------
// Lá, `ResumePreviewSwitch` caía no clássico no `default`, sem validar o que
// vinha do JSONB: um valor estranho trocava o tema do aluno em silêncio. Aqui a
// constraint da 000100 só aceita os cinco, e `temaValido` é a mesma pergunta do
// lado da tela — para o aluno saber antes de mandar, não depois.

export type Tema = "classico" | "moderno" | "minimalista" | "executivo" | "criativo";

export type Experiencia = {
  cargo: string;
  empresa: string;
  local: string;
  periodo: string;
  descricao: string;
};
export type Formacao = {
  titulo: string;
  curso: string;
  instituicao: string;
  local: string;
  periodo: string;
};
export type Curso = { nome: string; instituicao: string };
export type Idioma = { nome: string; nivel: string };

export type Curriculo = {
  id: string;
  titulo: string;
  tema: Tema;
  nome: string;
  email: string;
  telefone: string;
  /** HANDLE, nunca URL — vira href. Veja `enderecoDoLinkedin`. */
  linkedin: string;
  localidade: string;
  resumo: string;
  habilidades: string;
  informacoes_adicionais: string;
  experiencias: Experiencia[];
  formacoes: Formacao[];
  cursos: Curso[];
  idiomas: Idioma[];
  atualizado_em: string;
};

export const NIVEIS_IDIOMA = [
  "Básico",
  "Intermediário",
  "Avançado",
  "Fluente",
  "Nativo",
  "Técnico",
];

export const MESES = [
  { valor: "01", nome: "Janeiro" },
  { valor: "02", nome: "Fevereiro" },
  { valor: "03", nome: "Março" },
  { valor: "04", nome: "Abril" },
  { valor: "05", nome: "Maio" },
  { valor: "06", nome: "Junho" },
  { valor: "07", nome: "Julho" },
  { valor: "08", nome: "Agosto" },
  { valor: "09", nome: "Setembro" },
  { valor: "10", nome: "Outubro" },
  { valor: "11", nome: "Novembro" },
  { valor: "12", nome: "Dezembro" },
];

// ---------------------------------------------------------------------------
// Os cinco temas
// ---------------------------------------------------------------------------
// `layout` diz a ESTRUTURA, e ela é o que muda de verdade entre eles:
//   · `uma-coluna`   — clássico e minimalista
//   · `barra-lateral`— moderno e criativo: contato e listas curtas na barra
//   · `faixa`        — executivo: filete escuro na borda, conteúdo em coluna só
//
// `naBarra` só é lido nos temas de barra lateral. `resumoNoCabecalho` existe
// porque o criativo põe o resumo dentro da faixa do topo e NÃO tem seção
// "Resumo Profissional" — é diferença de conteúdo, não de cor.

export type Secao = "habilidades" | "idiomas" | "cursos";

export type DescricaoDoTema = {
  id: Tema;
  nome: string;
  descricao: string;
  layout: "uma-coluna" | "barra-lateral" | "faixa";
  fonte: string;
  /** Cor dos títulos e dos filetes. */
  tinta: string;
  /** Cor de destaque; vazio no minimalista, que não tem nenhuma. */
  destaque: string;
  /** Corpo do texto. */
  corpo: string;
  /** Secundário: datas, locais. */
  suave: string;
  /** O recheio da folha. */
  recheio: string;
  /** Filete sob o título de seção. Vazio = sem filete. */
  filete: string;
  tituloTamanho: number;
  tituloEspacamento: number;
  nomeTamanho: number;
  nomePeso: number | string;
  nomeCaixaAlta: boolean;
  /** Clássico e moderno gritam empresa e instituição em caixa alta. */
  gritaOrganizacao: boolean;
  italicoNoSecundario: boolean;
  naBarra: Secao[];
  resumoNoCabecalho: boolean;
  cabecalhoCentralizado: boolean;
};

export const TEMAS: DescricaoDoTema[] = [
  {
    id: "classico",
    nome: "Clássico",
    descricao: "Tradicional, ideal para tribunais e concursos.",
    layout: "uma-coluna",
    fonte: "Arial, Helvetica, sans-serif",
    tinta: "#1a3a5c",
    destaque: "#1a3a5c",
    corpo: "#333333",
    suave: "#666666",
    recheio: "30px 36px",
    filete: "2px solid #1a3a5c",
    tituloTamanho: 12,
    tituloEspacamento: 2,
    nomeTamanho: 26,
    nomePeso: "bold",
    nomeCaixaAlta: true,
    gritaOrganizacao: true,
    italicoNoSecundario: false,
    naBarra: [],
    resumoNoCabecalho: false,
    cabecalhoCentralizado: true,
  },
  {
    id: "moderno",
    nome: "Moderno",
    descricao: "Barra lateral em destaque, visual contemporâneo.",
    layout: "barra-lateral",
    fonte: "Arial, Helvetica, sans-serif",
    tinta: "#1a3a5c",
    destaque: "#1a3a5c",
    corpo: "#333333",
    suave: "#666666",
    recheio: "0",
    filete: "2px solid #1a3a5c",
    tituloTamanho: 12,
    tituloEspacamento: 2,
    nomeTamanho: 20,
    nomePeso: "bold",
    nomeCaixaAlta: false,
    gritaOrganizacao: true,
    italicoNoSecundario: false,
    naBarra: ["habilidades", "idiomas"],
    resumoNoCabecalho: false,
    cabecalhoCentralizado: false,
  },
  {
    id: "minimalista",
    nome: "Minimalista",
    descricao: "Sem cores, tipografia leve e espaçamento generoso.",
    layout: "uma-coluna",
    fonte: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    tinta: "#111111",
    // O único sem cor de destaque. Vazio é o valor, não um esquecimento.
    destaque: "",
    corpo: "#333333",
    suave: "#888888",
    recheio: "56px 64px",
    filete: "",
    tituloTamanho: 10,
    tituloEspacamento: 3,
    nomeTamanho: 30,
    nomePeso: 300,
    nomeCaixaAlta: false,
    gritaOrganizacao: false,
    italicoNoSecundario: false,
    naBarra: [],
    resumoNoCabecalho: false,
    cabecalhoCentralizado: false,
  },
  {
    id: "executivo",
    nome: "Executivo",
    descricao: "Serifado e formal, com faixa lateral escura.",
    layout: "faixa",
    fonte: 'Georgia, "Times New Roman", Times, serif',
    tinta: "#2c2c2c",
    destaque: "#2c2c2c",
    corpo: "#333333",
    suave: "#666666",
    recheio: "40px 44px",
    filete: "1px solid #2c2c2c",
    tituloTamanho: 11,
    tituloEspacamento: 4,
    nomeTamanho: 26,
    nomePeso: "bold",
    nomeCaixaAlta: false,
    gritaOrganizacao: false,
    italicoNoSecundario: true,
    naBarra: [],
    resumoNoCabecalho: false,
    cabecalhoCentralizado: false,
  },
  {
    id: "criativo",
    nome: "Criativo",
    descricao: "Cor de destaque e duas colunas para se destacar.",
    layout: "barra-lateral",
    fonte: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    tinta: "#1f2937",
    destaque: "#d97706",
    corpo: "#333333",
    suave: "#6b7280",
    recheio: "0",
    filete: "",
    tituloTamanho: 11,
    tituloEspacamento: 2,
    nomeTamanho: 30,
    nomePeso: 800,
    nomeCaixaAlta: false,
    gritaOrganizacao: false,
    italicoNoSecundario: false,
    naBarra: ["habilidades", "idiomas", "cursos"],
    resumoNoCabecalho: true,
    cabecalhoCentralizado: false,
  },
];

export function temaValido(valor: string): valor is Tema {
  return TEMAS.some((t) => t.id === valor);
}

export function descricaoDoTema(valor: string): DescricaoDoTema {
  return TEMAS.find((t) => t.id === valor) ?? TEMAS[0];
}

// ---------------------------------------------------------------------------
// O LinkedIn
// ---------------------------------------------------------------------------
// O banco guarda o HANDLE e o endereço é montado a partir de um prefixo FIXO,
// com o handle percent-encoded. Duas peças, e nenhuma sozinha basta:
//
//   · o formato barra os caracteres ESTRUTURAIS de URL, que é o que impede
//     `javascript:` e `//outro.site` chegarem a um href (000101);
//   · `encodeURIComponent` torna inerte qualquer coisa que escapasse.
//
// No app de origem não havia nem uma nem outra: `normalizeLinkedinUrl` prefixava
// `https://` e punha a string do aluno direto no `href` — e também dentro do
// PDF, via `pdf.link()`, sempre com o texto visível "LinkedIn". Um valor
// `//evil.com` virava `https:////evil.com`, que o navegador normaliza para
// `https://evil.com`: um link de phishing dentro de um documento que o aluno
// manda para recrutador, com o rótulo "LinkedIn" na frente.

const PREFIXO_LINKEDIN = "https://www.linkedin.com/in/";

/** Mesma regra de `handle_de_rede_valido` no banco (000101). Cortesia, não autoridade. */
export function linkedinValido(handle: string): boolean {
  const h = (handle ?? "").trim();
  if (h.length < 1 || h.length > 60) return false;
  if (/[\s:/?#%@\\&=<>"'`]/.test(h)) return false;
  return /[\p{L}\p{N}]/u.test(h);
}

/**
 * O handle a partir do que o aluno colou. Aceita a URL inteira, o
 * `linkedin.com/in/…` sem esquema, o `@handle` e o handle puro — porque é isso
 * que as pessoas colam, e recusar sem ajudar é fazer o aluno adivinhar.
 *
 * Devolve `null` quando não sai handle. Dos 268 valores medidos no app de
 * origem, esta função recupera 254; os 14 restantes são texto livre (a pessoa
 * digitou o próprio nome, com espaço).
 */
export function handleDoLinkedin(bruto: string): string | null {
  const s = (bruto ?? "").trim();
  if (s === "") return null;

  const m = /linkedin\.com\/(?:in|pub|company)\/?([^/?#\s]+)/i.exec(s);
  let candidato = m ? m[1] : s.replace(/^@/, "");

  // `jo%C3%A3o-silva` é como o navegador trafega acento. O banco guarda o handle
  // LEGÍVEL e o front re-codifica ao montar o href; guardar já codificado daria
  // codificação dupla e link quebrado.
  try {
    if (/%[0-9A-Fa-f]{2}/.test(candidato)) candidato = decodeURIComponent(candidato);
  } catch {
    return null;
  }

  candidato = candidato.replace(/\/+$/, "").trim();
  return linkedinValido(candidato) ? candidato : null;
}

/** O endereço completo, com prefixo fixo e handle codificado. */
export function enderecoDoLinkedin(handle: string): string {
  const h = (handle ?? "").trim();
  if (h === "" || !linkedinValido(h)) return "";
  return PREFIXO_LINKEDIN + encodeURIComponent(h);
}

// ---------------------------------------------------------------------------
// Texto e período
// ---------------------------------------------------------------------------

/**
 * As linhas de um campo multilinha, sem as vazias.
 *
 * O documento faz isso vinte vezes (resumo, descrição, habilidades, informações
 * adicionais). É o jeito de o currículo ter listas sem que o aluno digite
 * marcação nenhuma — e é por isso que o gerador nunca precisou de HTML no meio
 * do texto.
 */
export function linhasDe(texto: string): string[] {
  return (texto ?? "")
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l !== "");
}

export type Periodo = {
  mesInicio: string;
  anoInicio: string;
  mesFim: string;
  anoFim: string;
  atual: boolean;
};

const PERIODO_VAZIO: Periodo = {
  mesInicio: "",
  anoInicio: "",
  mesFim: "",
  anoFim: "",
  atual: false,
};

/**
 * Lê `MM/AAAA - MM/AAAA` ou `MM/AAAA - Atual` ou `MM/AAAA`.
 *
 * É o formato que os 579 currículos do app de origem já têm gravado, então a
 * leitura tem de aceitar as três formas — inclusive a de início solto, que
 * aparece quando o aluno não preencheu o fim.
 */
export function partesDoPeriodo(periodo: string): Periodo {
  const s = (periodo ?? "").trim();
  if (s === "") return { ...PERIODO_VAZIO };

  const [inicio, fim] = s.split(" - ").map((p) => (p ?? "").trim());
  const casaInicio = /^(\d{2})\/(\d{4})$/.exec(inicio ?? "");
  if (!casaInicio) return { ...PERIODO_VAZIO };

  if (fim === undefined || fim === "") {
    return { mesInicio: casaInicio[1], anoInicio: casaInicio[2], mesFim: "", anoFim: "", atual: false };
  }
  if (/^atual$/i.test(fim)) {
    return { mesInicio: casaInicio[1], anoInicio: casaInicio[2], mesFim: "", anoFim: "", atual: true };
  }
  const casaFim = /^(\d{2})\/(\d{4})$/.exec(fim);
  return {
    mesInicio: casaInicio[1],
    anoInicio: casaInicio[2],
    mesFim: casaFim ? casaFim[1] : "",
    anoFim: casaFim ? casaFim[2] : "",
    atual: false,
  };
}

/** Monta o texto do período de volta. Sem início, não há período. */
export function textoDoPeriodo(p: Periodo): string {
  if (!p.mesInicio || !p.anoInicio) return "";
  const inicio = `${p.mesInicio}/${p.anoInicio}`;
  if (p.atual) return `${inicio} - Atual`;
  if (p.mesFim && p.anoFim) return `${inicio} - ${p.mesFim}/${p.anoFim}`;
  return inicio;
}

/** Os anos que os seletores oferecem: os 40 até o corrente. */
export function anosDoPeriodo(agora: Date): string[] {
  const ano = agora.getFullYear();
  return Array.from({ length: 41 }, (_, i) => String(ano + 1 - i));
}

// ---------------------------------------------------------------------------
// O que está preenchido
// ---------------------------------------------------------------------------
// O documento só desenha a seção que tem conteúdo — e o painel usa a mesma
// contagem para dizer ao aluno o que falta. É UMA definição de "preenchido",
// não duas: uma seção que aparece no documento e não conta no painel (ou o
// contrário) faz o aluno procurar o que não existe.

export type Preenchimento = {
  temIdentificacao: boolean;
  temContato: boolean;
  temResumo: boolean;
  experiencias: number;
  formacoes: number;
  cursos: number;
  idiomas: number;
  temHabilidades: boolean;
  temInformacoes: boolean;
  /** Quantas das oito partes têm conteúdo. */
  partesPreenchidas: number;
  /** Sem nome, não há currículo para mostrar nem para baixar. */
  podeBaixar: boolean;
};

const temTexto = (v: string) => (v ?? "").trim() !== "";

/** A experiência entra no documento quando tem CARGO — é a regra do app de origem. */
export function experienciasUteis(lista: Experiencia[]): Experiencia[] {
  return (lista ?? []).filter((e) => temTexto(e?.cargo));
}
/** A formação entra quando tem CURSO. */
export function formacoesUteis(lista: Formacao[]): Formacao[] {
  return (lista ?? []).filter((f) => temTexto(f?.curso));
}
export function cursosUteis(lista: Curso[]): Curso[] {
  return (lista ?? []).filter((c) => temTexto(c?.nome));
}
export function idiomasUteis(lista: Idioma[]): Idioma[] {
  return (lista ?? []).filter((i) => temTexto(i?.nome));
}

export function preenchimentoDe(c: Curriculo): Preenchimento {
  const p: Preenchimento = {
    temIdentificacao: temTexto(c.nome),
    temContato: temTexto(c.email) || temTexto(c.telefone) || temTexto(c.linkedin),
    temResumo: temTexto(c.resumo),
    experiencias: experienciasUteis(c.experiencias).length,
    formacoes: formacoesUteis(c.formacoes).length,
    cursos: cursosUteis(c.cursos).length,
    idiomas: idiomasUteis(c.idiomas).length,
    temHabilidades: linhasDe(c.habilidades).length > 0,
    temInformacoes: linhasDe(c.informacoes_adicionais).length > 0,
    partesPreenchidas: 0,
    podeBaixar: temTexto(c.nome),
  };
  p.partesPreenchidas = [
    p.temIdentificacao,
    p.temContato,
    p.temResumo,
    p.experiencias > 0,
    p.formacoes > 0,
    p.cursos > 0,
    p.idiomas > 0,
    p.temHabilidades,
  ].filter(Boolean).length;
  return p;
}

/** Linha vazia de cada lista, para o botão "adicionar". */
export const EXPERIENCIA_VAZIA: Experiencia = {
  cargo: "",
  empresa: "",
  local: "",
  periodo: "",
  descricao: "",
};
export const FORMACAO_VAZIA: Formacao = {
  titulo: "",
  curso: "",
  instituicao: "",
  local: "",
  periodo: "",
};
export const CURSO_VAZIO: Curso = { nome: "", instituicao: "" };
export const IDIOMA_VAZIO: Idioma = { nome: "", nivel: "" };

/**
 * Limpa a lista antes de mandar ao banco: item totalmente vazio não vale uma
 * linha, e a RPC só aceita as chaves conhecidas. Sem isto, o botão "adicionar"
 * gravaria linhas em branco que o documento não desenha — e o aluno veria o
 * contador subir sem nada aparecer.
 */
export function paraGravar<T extends object>(lista: T[], chaves: (keyof T)[]): T[] {
  return (lista ?? [])
    .map((item) => {
      const limpo = {} as T;
      for (const k of chaves) {
        const v = item?.[k];
        limpo[k] = (typeof v === "string" ? v.trim() : (v ?? "")) as T[keyof T];
      }
      return limpo;
    })
    .filter((item) => chaves.some((k) => String(item[k] ?? "").trim() !== ""));
}

export const CHAVES_EXPERIENCIA: (keyof Experiencia)[] = [
  "cargo",
  "empresa",
  "local",
  "periodo",
  "descricao",
];
export const CHAVES_FORMACAO: (keyof Formacao)[] = [
  "titulo",
  "curso",
  "instituicao",
  "local",
  "periodo",
];
export const CHAVES_CURSO: (keyof Curso)[] = ["nome", "instituicao"];
export const CHAVES_IDIOMA: (keyof Idioma)[] = ["nome", "nivel"];

/**
 * Um currículo de EXEMPLO, para a galeria dos cinco temas na tela de quem ainda
 * não tem nenhum (redesenho de 26/08).
 *
 * Ficção declarada, como os documentos do Simulador MEI: o nome não é de
 * ninguém, o e-mail é `@exemplo.com` (domínio reservado pela RFC 2606, não
 * entrega para lugar algum), o LinkedIn é um handle que a tela nunca torna
 * clicável dentro de uma miniatura `inert`. Nunca é gravado: `criarCurriculo`
 * nasce do PERFIL do aluno, e este objeto só existe para a folha ter o que
 * desenhar antes de ele ter dados.
 *
 * Os textos são curtos de propósito — miniatura é para reconhecer a forma do
 * tema, e um exemplo comprido faria os cinco parecerem iguais de longe.
 */
export const CURRICULO_EXEMPLO: Curriculo = {
  id: "exemplo",
  titulo: "Exemplo",
  tema: "classico",
  nome: "Maria da Silva",
  email: "maria@exemplo.com",
  telefone: "(11) 90000-0000",
  linkedin: "maria-exemplo",
  localidade: "Campinas, SP",
  resumo:
    "Contadora com foco em rotinas fiscais do Simples Nacional e escrituração de MEI. Experiência em atendimento a pequenas empresas.",
  habilidades: "Apuração do Simples Nacional\nSPED Fiscal\nExcel avançado\nDomínio Sistemas",
  informacoes_adicionais: "Disponibilidade para viagens",
  experiencias: [
    {
      cargo: "Assistente fiscal",
      empresa: "Escritório Contábil Exemplo",
      local: "Campinas, SP",
      periodo: "03/2023 - Atual",
      descricao: "Apuração mensal de tributos de 40 empresas\nEntrega de obrigações acessórias",
    },
    {
      cargo: "Auxiliar administrativo",
      empresa: "Comércio Exemplo Ltda",
      local: "Campinas, SP",
      periodo: "01/2021 - 02/2023",
      descricao: "Conferência de notas fiscais\nConciliação bancária",
    },
  ],
  formacoes: [
    {
      titulo: "Bacharelado",
      curso: "Ciências Contábeis",
      instituicao: "Universidade Exemplo",
      local: "Campinas, SP",
      periodo: "02/2019 - 12/2022",
    },
  ],
  cursos: [{ nome: "Escola Contábil", instituicao: "Contabilidade Facilitada" }],
  idiomas: [{ nome: "Inglês", nivel: "Intermediário" }],
  atualizado_em: "",
};
